import { Button, Typography } from '@breetometer/components';
import { yupResolver } from '@hookform/resolvers/yup';
import { PinIcon } from 'BreetAssets';
import { Otp } from 'BreetComponents';
import { handleApiError, PINLENGTH, PinVerificationSchema, type PinVerificationSchemaType } from 'BreetHelpers';
import { useLazyVerifyPinQuery } from 'BreetRedux';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';

import type { IFormPinValidation } from './FormPinValidation.helpers';

const Child = ({ onSuccess, type, onResetPin }: IFormPinValidation & { onResetPin: () => void }) => {
	const [verifyPin, { isLoading }] = useLazyVerifyPinQuery();

	const {
		handleSubmit,
		setValue,
		formState: { errors, isValid },
	} = useForm<PinVerificationSchemaType>({
		resolver: yupResolver(PinVerificationSchema),
	});

	const handlePinVerification = useCallback(
		(data: PinVerificationSchemaType) => {
			if (type === 'collect') {
				onSuccess?.(data.pin);
			} else {
				verifyPin({ pin: data.pin })
					.unwrap()
					.then(() => {
						onSuccess?.(data.pin);
					})
					.catch(handleApiError);
			}
		},
		[onSuccess, type, verifyPin]
	);

	const onChangeInput = useCallback(
		(pin: string) => {
			setValue('pin', pin, { shouldValidate: true, shouldDirty: true });

			if (pin.length === PINLENGTH) {
				handlePinVerification({ pin });
			}
		},
		[handlePinVerification, setValue]
	);

	return (
		<div className='verifypinverification'>
			<PinIcon />

			<Typography
				as='h1'
				className='color-neutral-50'
			>
				Enter Transaction PIN
			</Typography>

			<form onSubmit={handleSubmit(handlePinVerification)}>
				<Typography
					as='label'
					className='color-primary-50'
				>
					Enter your {PINLENGTH}-digit PIN to complete this transaction.
				</Typography>

				<Otp.Input
					otpLength={PINLENGTH}
					position='center'
					onChange={onChangeInput}
					error={errors.pin?.message}
					maskInputValue
					showError
					data-testid='otp-input'
				/>

				<Button
					label='Continue'
					type='submit'
					disabled={!isValid}
					isLoading={isLoading}
				/>
			</form>

			<Typography
				as='span'
				className='forgot-pin color-primary-50'
			>
				Having troubles? Reset PIN in{' '}
				<Button
					onClick={onResetPin}
					variant='native'
					className='btn-link'
				>
					Account Settings
				</Button>
			</Typography>
		</div>
	);
};

export default Child;
